import React, { forwardRef } from 'react'
import type {
  DraggableProvidedDraggableProps,
  DraggableProvidedDragHandleProps,
} from 'react-beautiful-dnd'
import { useDispatch, useSelector } from 'react-redux'
import { KibChevronDownIcon, KibChevronUpIcon } from '@chewy/kib-icons-react'
import classNames from 'classnames'
import { useDebouncedCallback } from 'use-debounce'
import {
  Defaults,
  LanguageUtils,
  type Nil,
  NumberUtils,
} from '@pbt/pbt-ui-components'

import type {
  ModifiedChargeSheetLineItemInput,
  RetailOrderLineItem,
  UpdateRetailOrderLineItemInput,
} from '~/api/graphql/generated/types'
import { KibUserSelect } from '~/components/common/inputs/user-select/KibUserSelect'
import {
  calculatePrepaidColumns,
  getIsRetailOrderLineItem,
} from '~/components/dashboard/invoices/invoiceUtils'
import ChargesActions from '~/components/dashboard/invoices/payment/add-payment-dialog-v2/components/ChargesActions'
import DragHandleIcon from '~/components/dashboard/invoices/payment/add-payment-dialog-v2/components/DragHandleIcon'
import NumberField from '~/components/dashboard/invoices/payment/add-payment-dialog-v2/components/NumberField'
import { editRetailOrderLineItem } from '~/store/actions/finance'
import {
  editChargeSheetItem,
  editChargeSheetProducerItemBatch,
  editChargeSheetRetailOrderLineItemProducerBatch,
} from '~/store/duck/clientFinanceData'
import { useHasGroupedParentWithFixedPrice } from '~/store/hooks/charges'
import { getInvoiceGroupIdIsExpanded } from '~/store/reducers/finance'
import type { InvoiceLineItem } from '~/types'

import '@chewy/kib-fields-new-styles/dist/styles/kib-field.css'
import '@chewy/kib-fields-new-styles/dist/kib-fields-new.css'
import '@chewy/kib-controls-styles/dist/styles/kib-button-new.css'
import styles from './ChirpSoapChargesTable.module.scss'

export interface ChirpSoapChargesTableRowProps
  extends React.HTMLAttributes<HTMLTableRowElement> {
  bundleItems?: InvoiceLineItem[]
  dragHandleProps?: DraggableProvidedDragHandleProps | null
  draggableProps?: DraggableProvidedDraggableProps | null
  editDisabled?: boolean
  isBundle?: boolean
  isDragging?: boolean
  isNested?: boolean
  item: InvoiceLineItem | RetailOrderLineItem
  onBundleDeclineClick?: (item: InvoiceLineItem | RetailOrderLineItem) => void
  onBundleDeleteClick?: (item: InvoiceLineItem | RetailOrderLineItem) => void
  onCalculatorClick?: (item: InvoiceLineItem | RetailOrderLineItem) => void
  onDeclineClick?: (item: InvoiceLineItem | RetailOrderLineItem) => void
  onDeleteClick?: (item: InvoiceLineItem | RetailOrderLineItem) => void
  onToggleExpand?: (item: InvoiceLineItem | RetailOrderLineItem) => void
  parentItem?: InvoiceLineItem | RetailOrderLineItem
  soapId?: string | Nil
}

const ChirpSoapChargesTableRow = forwardRef<
  HTMLTableRowElement,
  ChirpSoapChargesTableRowProps
>(
  (
    {
      bundleItems,
      dragHandleProps,
      draggableProps,
      editDisabled = false,
      isBundle = false,
      isDragging = false,
      isNested = false,
      item,
      onBundleDeclineClick,
      onBundleDeleteClick,
      onCalculatorClick,
      onDeclineClick,
      onDeleteClick,
      onToggleExpand,
      parentItem,
      soapId,
      className,
    },
    ref,
  ) => {
    const dispatch = useDispatch()
    const isRetailOrderLineItem = getIsRetailOrderLineItem(item)
    const hasParentWithFixedPrice =
      useHasGroupedParentWithFixedPrice(parentItem)

    // Get expand state for bundles - use group property if available, fallback to id
    const groupId = isRetailOrderLineItem
      ? null
      : (item as InvoiceLineItem).group
    const expandKey = groupId || item.id
    const isExpanded = useSelector(
      getInvoiceGroupIdIsExpanded(expandKey, soapId),
    )

    // Get item name - bundles use groupName, regular items use name/nameTranslation
    const itemName = isBundle
      ? (item as InvoiceLineItem).groupName ||
        LanguageUtils.getTranslatedFieldName(item) ||
        ''
      : LanguageUtils.getTranslatedFieldName(item) || ''

    // Debounced handlers for quantity changes
    const handleEditInvoiceLineItem = useDebouncedCallback(
      (payload: { quantity: number }) => {
        const data: ModifiedChargeSheetLineItemInput = {
          ...payload,
          prepaid: (item as InvoiceLineItem).prepaid,
          id: item.id,
          expectedModification: item.modificationDate,
        }
        dispatch(editChargeSheetItem({ updateItemInput: [data] }))
      },
      Defaults.DEBOUNCE_ACTION_TIME,
    )

    const handleEditRetailOrderLineItem = useDebouncedCallback(
      (payload: { quantity: number }) => {
        const retailItem = item as RetailOrderLineItem
        const data: UpdateRetailOrderLineItemInput = {
          ...payload,
          id: retailItem.id,
          quantity: payload.quantity,
          declined: retailItem.declined,
          partNumber: retailItem.partNumber,
          expectedModificationDate: retailItem.modificationDate,
          logExpectedModificationDate: retailItem.prescription?.updatedAt,
          autoshipFrequency: retailItem.autoshipFrequency,
          autoshipUnitId: retailItem.autoshipUnit?.id,
        }
        dispatch(editRetailOrderLineItem(data))
      },
      Defaults.DEBOUNCE_ACTION_TIME,
    )

    const handleQuantityChange = (value: string) => {
      const numericValue = parseFloat(value)

      // Only trigger API call for valid numbers >= minimum
      if (Number.isNaN(numericValue) || numericValue < 0.01) {
        return
      }

      if (isRetailOrderLineItem) {
        handleEditRetailOrderLineItem({ quantity: numericValue })
      } else if ((item as InvoiceLineItem).prepaid) {
        // For prepaid items, calculatePrepaidColumns may adjust quantity
        // when prepaidRemaining would go negative
        const newItem = { ...item, quantity: numericValue } as InvoiceLineItem
        calculatePrepaidColumns(item as InvoiceLineItem, newItem, false)
        handleEditInvoiceLineItem({ quantity: newItem.quantity })
      } else {
        handleEditInvoiceLineItem({ quantity: numericValue })
      }
    }

    // Producer change handler
    const handleProducerChange = (producerId: string) => {
      if (isRetailOrderLineItem) {
        dispatch(
          editChargeSheetRetailOrderLineItemProducerBatch([
            {
              id: item.id,
              producerId: producerId || '',
              expectedModification: item.modificationDate,
            },
          ]),
        )
      } else {
        dispatch(
          editChargeSheetProducerItemBatch({
            clientId: (item as InvoiceLineItem).clientId,
            updateItemProducerInput: [
              {
                producerId,
                id: item.id,
                expectedModification: item.modificationDate,
              },
            ],
          }),
        )
      }
    }

    const handleExpandClick = (e: React.MouseEvent) => {
      e.stopPropagation()
      if (onToggleExpand) {
        onToggleExpand(item)
      }
    }

    // NOTE: Must return single <tr> element for react-beautiful-dnd to work correctly.
    // For bundles with children, we render them inside a nested table within a single <td colspan>
    if (isBundle && bundleItems && bundleItems.length > 0) {
      return (
        <tr
          className={classNames(
            styles.dataRow,
            {
              [styles.dataRowDragging]: isDragging,
              [styles.declined]: item.declined,
            },
            className,
          )}
          ref={ref}
          {...draggableProps}
          {...dragHandleProps}
        >
          <td className={styles.bundleCell} colSpan={5}>
            {/* Parent bundle row content - using table for proper column alignment */}
            <table className={styles.bundleParentTable}>
              <colgroup>
                <col style={{ width: '307px' }} />
                <col style={{ width: '146px' }} />
                <col style={{ width: '55px' }} />
                <col style={{ width: '110px' }} />
                <col style={{ width: '77px' }} />
              </colgroup>
              <tbody>
                <tr className={styles.bundleParentRow}>
                  {/* Item column with drag handle and expand button */}
                  <td
                    className={classNames(styles.dataCell, styles.dataCellItem)}
                  >
                    <span className={styles.dragHandle}>
                      <DragHandleIcon size={24} />
                    </span>
                    <button
                      aria-label={
                        isExpanded ? 'Collapse bundle' : 'Expand bundle'
                      }
                      className={styles.expandButton}
                      type="button"
                      onClick={handleExpandClick}
                    >
                      {isExpanded ? (
                        <KibChevronUpIcon />
                      ) : (
                        <KibChevronDownIcon />
                      )}
                    </button>
                    <span className={styles.itemName} title={itemName}>
                      {itemName}
                    </span>
                  </td>

                  {/* Empty Producer column */}
                  <td
                    aria-label="Producer not applicable for bundles"
                    className={styles.dataCell}
                  />

                  {/* Empty Quantity column */}
                  <td
                    aria-label="Quantity not applicable for bundles"
                    className={styles.dataCell}
                  />

                  {/* Actions column */}
                  <td
                    className={classNames(
                      styles.dataCell,
                      styles.dataCellActions,
                    )}
                  >
                    <ChargesActions
                      showDeclineButton
                      showDeleteButton
                      declineHighlighted={item.declined}
                      disabledCalculator={editDisabled}
                      disabledDecline={editDisabled}
                      disabledDelete={editDisabled}
                      showCalculatorButton={false}
                      onDeclineClick={() => onBundleDeclineClick?.(item)}
                      onDeleteClick={() => onBundleDeleteClick?.(item)}
                    />
                  </td>

                  {/* Total column */}
                  <td
                    className={classNames(
                      styles.dataCell,
                      styles.dataCellTotal,
                    )}
                  >
                    <span className={styles.price}>
                      {NumberUtils.formatMoney(item.subTotal)}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>

            {/* Nested table for bundle children */}
            {isExpanded && (
              <table className={styles.nestedTable}>
                <tbody>
                  {bundleItems.map((childItem) => (
                    <ChirpSoapChargesTableRow
                      isNested
                      editDisabled={editDisabled}
                      item={childItem}
                      key={`${childItem.bundleId}_${childItem.id}`}
                      parentItem={item as InvoiceLineItem}
                      soapId={soapId}
                      onCalculatorClick={onCalculatorClick}
                      onDeclineClick={onDeclineClick}
                      onDeleteClick={onDeleteClick}
                    />
                  ))}
                </tbody>
              </table>
            )}
          </td>
        </tr>
      )
    }

    // Regular (non-bundle) or nested row
    return (
      <tr
        className={classNames(
          styles.dataRow,
          {
            [styles.dataRowDragging]: isDragging,
            [styles.dataRowNested]: isNested,
            [styles.declined]: item.declined,
          },
          className,
        )}
        ref={ref}
        {...draggableProps}
        {...dragHandleProps}
      >
        {/* Item cell with drag handle and expand icon */}
        <td className={styles.dataCell}>
          <div
            className={classNames(styles.dataCellItem, {
              [styles.dataCellItemNested]: isNested,
            })}
          >
            {!isNested && (
              <span className={styles.dragHandle}>
                <DragHandleIcon size={24} />
              </span>
            )}
            {isBundle && (
              <button
                aria-label={isExpanded ? 'Collapse bundle' : 'Expand bundle'}
                className={styles.expandButton}
                type="button"
                onClick={handleExpandClick}
              >
                {isExpanded ? <KibChevronUpIcon /> : <KibChevronDownIcon />}
              </button>
            )}
            <span className={styles.itemName} title={itemName}>
              {itemName}
            </span>
          </div>
        </td>

        {/* Producer cell */}
        <td
          className={classNames(styles.dataCell, styles.dataCellProducer)}
          onClick={(e) => e.stopPropagation()}
        >
          {!item.declined && !isBundle && (
            <KibUserSelect
              reset
              disabled={editDisabled}
              placeHolder=""
              userSelectStateId={`producer-${item.id}`}
              value={item.producerId || ''}
              onChange={handleProducerChange}
            />
          )}
        </td>

        {/* Quantity cell */}
        <td
          className={classNames(styles.dataCell, styles.dataCellQuantity)}
          onClick={(e) => e.stopPropagation()}
        >
          {!item.declined && !isBundle && (
            <NumberField
              disabled={editDisabled}
              label=""
              min={0.01}
              step="0.01"
              value={String(item.quantity || 0)}
              onChange={handleQuantityChange}
            />
          )}
        </td>

        {/* Actions cell */}
        <td
          className={classNames(styles.dataCell, styles.dataCellActions)}
          onClick={(e) => e.stopPropagation()}
        >
          {!isBundle && (
            <ChargesActions
              declineHighlighted={item.declined}
              disabledCalculator={editDisabled}
              disabledDecline={editDisabled}
              disabledDelete={editDisabled}
              showCalculatorButton={!hasParentWithFixedPrice}
              onCalculatorClick={() => onCalculatorClick?.(item)}
              onDeclineClick={() => onDeclineClick?.(item)}
              onDeleteClick={() => onDeleteClick?.(item)}
            />
          )}
        </td>

        {/* Total cell */}
        <td className={classNames(styles.dataCell, styles.dataCellTotal)}>
          {!hasParentWithFixedPrice && (
            <span className={styles.price}>
              {NumberUtils.formatMoney(item.subTotal)}
            </span>
          )}
        </td>
      </tr>
    )
  },
)

export default ChirpSoapChargesTableRow
